import { Search } from "lucide-react";
import React, { ReactNode } from "react";
import { Updater } from "use-immer";

import {
  IgTableFilterButton,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  GetExperiencesListResponseBodyType,
  GetOffersExperienceListResponseType,
  PersonalizationOfferType,
  PersonalizationTestType,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { ShowArchivedCheckbox } from "@src/components/Table/ShowArchivedCheckbox";
import { UseQueryResult } from "@tanstack/react-query";

interface OffersTableHeaderProps {
  experienceData: UseQueryResult<
    GetOffersExperienceListResponseType | GetExperiencesListResponseBodyType,
    Error
  >;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  statusFilters: string[];
  setStatusFilters: Updater<string[]>;
  typeFilters: (PersonalizationOfferType | PersonalizationTestType)[];
  setTypeFilters: Updater<
    (PersonalizationOfferType | PersonalizationTestType)[]
  >;
  statusFilterListOptions: string[];
  typeFilterListOptions: string[];
  title: string;
  endButtons?: ReactNode;
}

export const SharedTableHeader = ({
  searchInput,
  setSearchInput,
  statusFilters,
  setStatusFilters,
  typeFilters,
  setTypeFilters,
  statusFilterListOptions,
  typeFilterListOptions,
  title,
  endButtons,
}: OffersTableHeaderProps) => {
  const onStatusFilterClick = (key: string) => {
    setStatusFilters((draft) => {
      if (statusFilters.includes(key)) {
        draft = draft.filter((filter) => filter !== key);
      } else {
        draft.push(key);
      }
      return draft;
    });
  };

  const onTypeFilterClick = (
    key: PersonalizationOfferType | PersonalizationTestType
  ) => {
    setTypeFilters((draft) => {
      if (typeFilters.includes(key)) {
        draft = draft.filter((filter) => filter !== key);
      } else {
        draft.push(key);
      }
      return draft;
    });
  };

  return (
    <Grid item container direction={"column"} columnSpacing={1} rowSpacing={4}>
      {endButtons ? (
        <Grid
          item
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item>
            <IgTypography variant={"h3"} fontWeight={600}>
              {title}
            </IgTypography>
          </Grid>
          <Grid item>{endButtons}</Grid>
        </Grid>
      ) : (
        <Grid item>
          <IgTypography variant={"h3"} fontWeight={600}>
            {title}
          </IgTypography>
        </Grid>
      )}
      <Grid
        container
        item
        direction={"row"}
        alignItems={"center"}
        columnSpacing={2}
      >
        <Grid item xs>
          <IgTextField
            fullWidth={true}
            placeholder={
              title === "Offers" ? "Search by offer name" : "Search by name"
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              endAdornment: <Search size={16} />,
            }}
          />
        </Grid>

        <Grid item xs={"auto"}>
          <IgTableFilterButton
            filterTitle={"Status"}
            activeFilters={statusFilters}
            options={statusFilterListOptions}
            onFilterClick={onStatusFilterClick}
          />
        </Grid>
        <Grid item xs={"auto"}>
          <IgTableFilterButton
            filterTitle={"Type"}
            activeFilters={typeFilters}
            options={typeFilterListOptions}
            onFilterClick={onTypeFilterClick}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
